import React, { useState, useEffect, useRef } from "react";
import * as api from "../api";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Input, Select, SectionHeader, Callout, IconBtn, Ref } from "./UI";

// Cible tactile minimale — écran utilisé debout, sur le terrain.
const TAP = { minHeight: 44 };

function ColForm({ setView, order, setCurrentOrder }) {
  const [clothType, setClothType] = useState("");
  const [qty, setQty]             = useState(1);
  const [items, setItems]         = useState([]);
  const [price, setPrice]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [photos, setPhotos]       = useState([]);
  const [payMethod, setPayMethod] = useState("cash");
  const [payStatus, setPayStatus] = useState("non_paye");
  const [amountPaid, setAmountPaid] = useState("");
  const [articlesList, setArticlesList] = useState([]);
  const [etabsList, setEtabsList] = useState([]);
  const [selectedEtab, setSelectedEtab] = useState("");
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [applyDiscount, setApplyDiscount] = useState(true);
  const [deliveryZone, setDeliveryZone] = useState("");
  const [feesConfig, setFeesConfig] = useState(null);
  const fileRef = useRef(null);

  // Load establishments + loyalty info
  useEffect(() => {
    api.adminGetEtablissements().then(data => {
      const active = (data || []).filter(e => e.active);
      setEtabsList(active);
      if (active.length === 1) setSelectedEtab(active[0].id.toString());
    }).catch(() => {});
    if (order?.phone) {
      api.getLoyalty(order.phone).then(l => setLoyaltyInfo(l)).catch(() => {});
    }
    api.getDeliveryFeesConfig().then(c => setFeesConfig(c)).catch(() => {});
  }, [order?.phone]);

  // Load articles from DB
  useEffect(() => {
    api.getArticles().then(data => {
      if (data && data.length) {
        setArticlesList(data);
        setClothType(data[0].name);
      }
    }).catch(() => {
      // Fallback
      const fallback = [
        { name: "Chemise", price: 1000 }, { name: "Pantalon", price: 1000 },
        { name: "Robe", price: 1500 }, { name: "Costume (2 pièces)", price: 3000 },
        { name: "T-shirt", price: 500 }, { name: "Autre", price: 1000 },
      ];
      setArticlesList(fallback);
      setClothType(fallback[0].name);
    });
  }, []);

  // Dynamic price map from articles
  const priceMap = {};
  articlesList.forEach(a => { priceMap[a.name] = parseFloat(a.price); });

  function addItem() {
    if (qty < 1 || !clothType) return;
    const unitPrice = priceMap[clothType] || 1000;
    setItems(prev => {
      const existing = prev.find(i => i.type === clothType);
      if (existing) return prev.map(i => i.type === clothType ? { ...i, qty: i.qty + Number(qty) } : i);
      return [...prev, { type: clothType, qty: Number(qty), unitPrice }];
    });
    setQty(1);
  }

  function removeItem(type) {
    setItems(prev => prev.filter(i => i.type !== type));
  }

  // Auto-calc total
  const articlesTotal = items.reduce((s, i) => s + i.qty * (i.unitPrice || priceMap[i.type] || 1000), 0);

  // Delivery fees calculation
  const isSurPlace = order?.order_type === "sur_place";
  const deliveryFee = (() => {
    if (isSurPlace || !feesConfig?.enabled || !deliveryZone) return 0;
    const fee = deliveryZone === "campus" ? (feesConfig.campus_fee || 500) : (feesConfig.hors_campus_fee || 1000);
    if (feesConfig.free_threshold > 0 && articlesTotal >= feesConfig.free_threshold) return 0;
    return fee;
  })();

  const autoTotal = articlesTotal + deliveryFee;

  useEffect(() => {
    if (items.length > 0) setPrice(String(autoTotal));
  }, [items, autoTotal, deliveryZone]);

  // Photo capture
  function handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotos(prev => [...prev, reader.result]);
    reader.readAsDataURL(file);
  }

  async function handleSubmit() {
    if (items.length === 0) { setError("Ajoutez au moins un article"); return; }
    if (!price || Number(price) <= 0) { setError("Entrez un prix valide"); return; }
    if (!isSurPlace && feesConfig?.enabled && !deliveryZone) { setError("Sélectionnez la zone de collecte (Campus ou Hors campus)"); return; }
    if (etabsList.length > 0 && !selectedEtab) { setError("Sélectionnez l'établissement de dépôt"); return; }
    setLoading(true); setError("");
    try {
      const itemsStr = items.map(i => `${i.type} x${i.qty}`).join(", ");
      const hasLoyaltyDiscount = applyDiscount && loyaltyInfo?.config?.enabled && loyaltyInfo?.applicableDiscount > 0;
      const finalPrice = hasLoyaltyDiscount ? Math.round(Number(price) * (1 - loyaltyInfo.applicableDiscount / 100)) : Number(price);
      const updateData = {
        items: itemsStr, price: finalPrice, status: "Collecté",
        collector_status: "arrived",
        payment_status: payStatus,
        payment_method: payMethod,
        amount_paid: payStatus === "paye" ? finalPrice : payStatus === "avance" ? Number(amountPaid) || 0 : 0,
        loyalty_discount: hasLoyaltyDiscount ? Number(price) - finalPrice : 0,
        delivery_zone: deliveryZone || null,
        delivery_fee: deliveryFee || 0,
      };
      if (selectedEtab) updateData.etab_id = parseInt(selectedEtab);
      const updated = await api.updateOrder(order.id, updateData);

      // Deduct loyalty points if discount applied
      if (hasLoyaltyDiscount && loyaltyInfo.applicableThreshold) {
        try { await api.useLoyaltyPoints(order.phone, loyaltyInfo.applicableThreshold.points, order.id); } catch {}
      }

      // Save order items via API
      try {
        await api.saveOrderItems(order.id, items.map(i => ({
          service_name: "Pressing",
          article_name: i.type,
          quantity: i.qty,
          unit_price: i.unitPrice || priceMap[i.type] || 1000,
        })));
      } catch { /* non-blocking */ }

      // Upload photos
      for (const photo of photos) {
        try { await api.uploadOrderPhoto(order.id, photo); } catch { /* non-blocking */ }
      }

      setCurrentOrder(updated);
      setView("col_recu");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Bouton de choix segmenté — cible tactile large, bordure 1px, aplat de couleur
  const segment = (active, tone) => ({
    ...TAP,
    flex: 1, minWidth: 104, padding: "11px 12px", borderRadius: R.md, cursor: "pointer",
    border: `1px solid ${active ? tone : C.border}`,
    background: active ? C.surfaceAlt : C.surface,
    color: active ? tone : C.inkBody,
    fontWeight: active ? F.weight.semi : F.weight.medium,
    fontSize: F.size.md,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
    transition: "border-color .15s ease, color .15s ease, background .15s ease",
  });

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      {/* ─── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{ background: C.navy, color: C.inkOn, padding: "12px 16px 16px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <Btn
            variant="ghost" icon="back"
            style={{ ...TAP, borderColor: "rgba(255,255,255,.4)", color: C.inkOn, marginBottom: 10 }}
            onClick={() => setView("col_list")}
          >
            Retour
          </Btn>
          <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.01em", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            Collecte
            <Ref style={{ color: C.inkOn, fontSize: F.size.lg }}>{order?.id}</Ref>
          </h1>
          <p style={{ fontSize: F.size.md, color: "rgba(255,255,255,.72)", marginTop: 2 }}>
            {order?.client} · <span style={numeric}>{order?.phone}</span>
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: 16 }}>
        {/* ─── Photos ────────────────────────────────────────────────────── */}
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader
            icon="camera"
            title="Photos des vêtements"
            subtitle="Documentez l'état des vêtements avant la collecte"
          />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position: "relative", width: 80, height: 80, borderRadius: R.md, overflow: "hidden", border: `1px solid ${C.border}` }}>
                <img src={p} alt={`Vêtement ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button
                  type="button"
                  title={`Supprimer la photo ${i + 1}`}
                  aria-label={`Supprimer la photo ${i + 1}`}
                  onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                  style={{
                    position: "absolute", top: 3, right: 3,
                    background: "rgba(10,37,64,.78)", color: C.inkOn, border: "none",
                    borderRadius: R.sm, width: 22, height: 22,
                    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                  }}
                >
                  <Icon name="close" size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              title="Prendre une photo"
              aria-label="Prendre une photo"
              onClick={() => fileRef.current?.click()}
              style={{
                width: 80, height: 80, borderRadius: R.md,
                border: `1px dashed ${C.borderStrong}`, background: C.surfaceAlt,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                color: C.inkMuted, cursor: "pointer", fontSize: F.size.xs, fontWeight: F.weight.medium,
              }}
            >
              <Icon name="camera" size={20} />
              Photo
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: "none" }} />
        </Card>

        {/* ─── Articles ──────────────────────────────────────────────────── */}
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader icon="order" title="Articles" />

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
            <Select
              label="Article"
              value={clothType}
              onChange={setClothType}
              style={{ flex: 1, minWidth: 160, marginBottom: 0 }}
            >
              {articlesList.map(a => (
                <option key={a.name} value={a.name}>{a.name} — {(parseFloat(a.price) || 1000).toLocaleString()} F</option>
              ))}
            </Select>
            <Input
              label="Qté"
              type="number"
              inputMode="numeric"
              value={qty}
              min={1}
              onChange={v => setQty(Number(v))}
              style={{ width: 78, marginBottom: 0 }}
            />
            <Btn icon="add" style={TAP} onClick={addItem}>Ajouter</Btn>
          </div>

          {items.length > 0 && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, overflow: "hidden", marginBottom: 16 }}>
              {items.map(item => (
                <div key={item.type} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                  padding: "9px 12px", borderBottom: `1px solid ${C.border}`,
                }}>
                  <span className="icon-text" style={{ fontSize: F.size.md, color: C.inkBody, minWidth: 0 }}>
                    <Icon name="garment" size={15} color={C.inkMuted} />
                    <span className="truncate">{item.type}</span>
                    <strong style={{ color: C.ink, ...numeric }}>× {item.qty}</strong>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: F.size.base, color: C.inkMuted, ...numeric }}>
                      {(item.qty * (item.unitPrice || priceMap[item.type] || 1000)).toLocaleString()} F
                    </span>
                    <IconBtn icon="delete" danger title={`Retirer ${item.type}`} onClick={() => removeItem(item.type)} />
                  </span>
                </div>
              ))}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                padding: "11px 12px", background: C.surfaceAlt,
              }}>
                <span style={{ fontSize: F.size.base, color: C.inkMuted, fontWeight: F.weight.medium }}>Total</span>
                <strong style={{ fontSize: F.size.lg, color: C.ink, ...numeric }}>{autoTotal.toLocaleString()} FCFA</strong>
              </div>
            </div>
          )}

          {/* Zone de collecte (uniquement pour les commandes livraison) */}
          {!isSurPlace && feesConfig?.enabled && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: F.weight.medium, marginBottom: 6, fontSize: F.size.base, color: C.inkBody }}>
                Zone de collecte
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  ["campus", "graduate", `Campus — ${feesConfig.campus_fee?.toLocaleString() || 500} F`, C.navy],
                  ["hors_campus", "home", `Hors campus — ${feesConfig.hors_campus_fee?.toLocaleString() || "1 000"} F`, C.amber],
                ].map(([k, icon, label, tone]) => (
                  <button
                    key={k} type="button" onClick={() => setDeliveryZone(k)}
                    aria-pressed={deliveryZone === k}
                    style={segment(deliveryZone === k, tone)}
                  >
                    <Icon name={icon} size={15} />
                    <span style={{ fontSize: F.size.base }}>{label}</span>
                  </button>
                ))}
              </div>
              {deliveryZone && deliveryFee === 0 && articlesTotal >= (feesConfig.free_threshold || 5000) && (
                <Callout tone="success" icon="gift" style={{ marginTop: 8 }}>
                  Livraison gratuite (total ≥ <span style={numeric}>{(feesConfig.free_threshold || 5000).toLocaleString()}</span> F)
                </Callout>
              )}
              {deliveryZone && deliveryFee > 0 && (
                <Callout tone="info" icon="collect" style={{ marginTop: 8 }}>
                  Frais de collecte / livraison : <strong style={{ color: C.ink, ...numeric }}>+{deliveryFee.toLocaleString()} F</strong>
                </Callout>
              )}
            </div>
          )}

          <Input
            label="Prix total (FCFA)"
            required
            type="number"
            inputMode="numeric"
            value={price}
            onChange={setPrice}
            placeholder="ex: 5000"
            min={0}
            hint="ajustable"
            style={{ marginBottom: 8 }}
          />
          {deliveryFee > 0 && (
            <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 4, ...numeric }}>
              Articles : {articlesTotal.toLocaleString()} F + Livraison : {deliveryFee.toLocaleString()} F = {autoTotal.toLocaleString()} F
            </p>
          )}
          <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginBottom: 16 }}>
            Le prix est calculé automatiquement, mais vous pouvez l'ajuster.
          </p>

          {/* Réduction fidélité */}
          {loyaltyInfo && loyaltyInfo.config?.enabled && loyaltyInfo.applicableDiscount > 0 && Number(price) > 0 && (
            <div style={{
              background: C.successSoft, border: `1px solid ${C.successBorder}`,
              borderRadius: R.md, padding: 13,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span className="icon-text" style={{ fontWeight: F.weight.semi, color: C.success, fontSize: F.size.md }}>
                  <Icon name="gift" size={15} />
                  Réduction fidélité (<span style={numeric}>{loyaltyInfo.applicableDiscount}</span> %)
                </span>
                <Btn
                  small
                  variant={applyDiscount ? "danger" : "success"}
                  onClick={() => setApplyDiscount(d => !d)}
                >
                  {applyDiscount ? "Retirer" : "Appliquer"}
                </Btn>
              </div>
              <div style={{ fontSize: F.size.base, color: C.inkBody }}>
                <span style={numeric}>{loyaltyInfo.points}</span> points · Palier <span style={numeric}>{loyaltyInfo.applicableThreshold?.points}</span> pts atteint
              </div>
              {applyDiscount && (
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontSize: F.size.base, color: C.inkMuted, textDecoration: "line-through", ...numeric }}>
                    {Number(price).toLocaleString()} FCFA
                  </span>
                  <strong style={{ fontSize: F.size.xl, fontWeight: F.weight.bold, color: C.success, ...numeric }}>
                    {Math.round(Number(price) * (1 - loyaltyInfo.applicableDiscount / 100)).toLocaleString()} FCFA
                  </strong>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ─── Mode de paiement ──────────────────────────────────────────── */}
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader icon="card" title="Mode de paiement" />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["cash", "cash", "Espèces"], ["wave", "waves", "Wave"], ["orange_money", "mobile", "Orange Money"]].map(([k, icon, label]) => (
              <button
                key={k} type="button" onClick={() => setPayMethod(k)}
                aria-pressed={payMethod === k}
                style={segment(payMethod === k, C.navy)}
              >
                <Icon name={icon} size={15} />
                {label}
              </button>
            ))}
          </div>
        </Card>

        {/* ─── Statut du paiement ────────────────────────────────────────── */}
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader icon="money" title="Statut du paiement" />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              ["non_paye", "error",   "Non payé", C.danger],
              ["avance",   "timer",   "Avance",   C.warning],
              ["paye",     "success", "Payé",     C.success],
            ].map(([k, icon, label, tone]) => (
              <button
                key={k} type="button" onClick={() => setPayStatus(k)}
                aria-pressed={payStatus === k}
                style={segment(payStatus === k, tone)}
              >
                <Icon name={icon} size={15} />
                {label}
              </button>
            ))}
          </div>

          {payStatus === "avance" && (
            <div style={{ marginTop: 14 }}>
              <Input
                label="Montant de l'avance (FCFA)"
                required
                type="number"
                inputMode="numeric"
                value={amountPaid}
                onChange={setAmountPaid}
                placeholder="ex: 3000"
                min={0}
                style={{ marginBottom: 0 }}
              />
              {price && amountPaid && Number(amountPaid) < Number(price) && (
                <Callout tone="warning" icon="info" style={{ marginTop: 8 }}>
                  Reste à payer : <strong style={{ color: C.ink, ...numeric }}>{(Number(price) - Number(amountPaid)).toLocaleString()} FCFA</strong>
                </Callout>
              )}
            </div>
          )}
          {payStatus === "paye" && price && (
            <Callout tone="success" icon="success" style={{ marginTop: 12 }}>
              Le client a payé la totalité : <strong style={{ color: C.ink, ...numeric }}>{Number(price).toLocaleString()} FCFA</strong>
            </Callout>
          )}
        </Card>

        {/* ─── Établissement de dépôt ────────────────────────────────────── */}
        {etabsList.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionHeader icon="building" title="Établissement de dépôt" subtitle="Où amenez-vous les vêtements ?" />
            <Select
              value={selectedEtab}
              onChange={setSelectedEtab}
              required
              placeholder="— Choisir l'établissement —"
              style={{ marginBottom: 0 }}
            >
              {etabsList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </Select>
          </Card>
        )}

        {error && <Callout tone="danger" icon="alert" style={{ marginBottom: 12 }}>{error}</Callout>}

        <Btn
          fullWidth icon="check"
          style={{ minHeight: 52, fontSize: F.size.lg }}
          onClick={handleSubmit}
          disabled={loading}
          loading={loading}
        >
          {loading ? "Validation…" : "Valider la collecte"}
        </Btn>
      </div>
    </div>
  );
}

export default ColForm;
